import {
  HttpBackend,
  HttpClient,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable, InjectionToken, NgModule } from '@angular/core';
import { Observable } from 'rxjs';
import {
  IdentityService,
  TOKENS as AUTHORIZATION_TOKENS,
} from '@trustedshops/tswp-core-authorization';

/**
 * Injection token for the custom HttpClient instance with authentication interceptor.
 * Use this token to inject the pre-configured HttpClient that automatically adds auth headers.
 */
export const ANGULAR_HTTP_CLIENT_TOKEN = new InjectionToken<HttpClient>(
  'ANGULAR_HTTP_CLIENT_TOKEN'
);

/**
 * Injection token for HTTP interceptors used by the authenticated HttpClient.
 * Similar to Angular's HTTP_INTERCEPTORS but specifically for our custom client.
 *
 * Use this token to provide additional interceptors that should be applied
 * to the authenticated HttpClient instance.
 */
export const ANGULAR_HTTP_CLIENT_INTERCEPTOR_TOKEN = new InjectionToken<
  HttpInterceptor[]
>('ANGULAR_HTTP_CLIENT_INTERCEPTOR_TOKEN');

/**
 * HTTP Interceptor that automatically adds Bearer authentication token to all requests.
 *
 * This interceptor:
 * - Retrieves the current auth token from the identity service
 * - Adds it as an Authorization header with Bearer scheme
 * - Handles cases where token might be unavailable
 */
@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
  constructor(
    @Inject(AUTHORIZATION_TOKENS.IdentityService)
    private readonly identityService: IdentityService
  ) {}

  /**
   * Intercepts HTTP requests and adds authentication headers.
   *
   * @param request - The outgoing HTTP request
   * @param next - The next handler in the interceptor chain
   * @returns Observable of the HTTP event
   */
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const authToken = this.getAuthToken();

    // Only add auth header if token is available
    if (!authToken) {
      return next.handle(request);
    }

    const authenticatedRequest = request.clone({
      setHeaders: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    return next.handle(authenticatedRequest);
  }

  /**
   * Safely extracts the authentication token from the identity service.
   *
   * @returns The JWT token string or null if not available
   */
  private getAuthToken(): string | null {
    return (
      this.identityService?.identity?.value?.profile?.keycloak?.token?.encoded
        ?.value || null
    );
  }
}

/**
 * Custom HTTP handler that chains multiple interceptors together.
 *
 * This handler processes interceptors in reverse order to maintain the correct
 * execution sequence where the first interceptor in the array runs first.
 */
export class ChainedHttpInterceptorHandler implements HttpHandler {
  constructor(
    private readonly nextHandler: HttpHandler,
    private readonly interceptors: readonly HttpInterceptor[]
  ) {}

  /**
   * Handles an HTTP request by chaining it through all configured interceptors.
   *
   * @param request - The HTTP request to handle
   * @returns Observable of the HTTP event
   */
  handle(request: HttpRequest<unknown>): Observable<HttpEvent<unknown>> {
    if (this.interceptors.length === 0) {
      return this.nextHandler.handle(request);
    }

    // Build the interceptor chain in reverse order
    let handler = this.nextHandler;

    for (let i = this.interceptors.length - 1; i >= 0; i--) {
      const interceptor = this.interceptors[i];
      const currentHandler = handler;

      handler = {
        handle: (req: HttpRequest<unknown>) =>
          interceptor.intercept(req, currentHandler),
      };
    }

    return handler.handle(request);
  }
}

/**
 * Factory function that creates an HttpClient instance with authentication interceptor.
 *
 * This function:
 * - Collects all interceptors provided via ANGULAR_HTTP_CLIENT_INTERCEPTOR_TOKEN token
 * - Creates a custom interceptor handler with the collected interceptors
 * - Returns a pre-configured HttpClient that automatically processes requests through interceptors
 *
 * @param httpBackend - The HTTP backend service
 * @param interceptors - Array of interceptors provided via ANGULAR_HTTP_CLIENT_INTERCEPTOR_TOKEN token
 * @returns Configured HttpClient with authentication interceptor
 */
export function createHttpClient(
  httpBackend: HttpBackend,
  interceptors: HttpInterceptor[]
): HttpClient {
  const interceptorHandler = new ChainedHttpInterceptorHandler(
    httpBackend,
    interceptors || []
  );

  return new HttpClient(interceptorHandler);
}

/**
 * Angular module that provides a pre-configured HttpClient with authentication.
 * This is a temporary workaround to be able using original Angular HttpClient
 * without custom logic. Main reason is that the custom HttpClientService
 * uses AbstractEvent under the hood which brakes native RxJS Observables behaviour.
 *
 * Only as a temporary solution we aim to use this service only in carrier-b2b.
 * It is not supposed to be reusable in other plugins.
 *
 * TODO: replace this approach with new service. Ticket reference: https://trustedshops.atlassian.net/browse/CC-688
 *
 * This module registers a custom HttpClient instance that automatically:
 * - Adds Bearer authentication tokens to requests
 * - Handles token extraction safely
 * - Provides proper error handling
 * - Supports additional interceptors via ANGULAR_HTTP_CLIENT_INTERCEPTOR_TOKEN token
 *
 * Usage:
 * 1. Import this module in your app module
 * 2. Inject AUTHENTICATED_HTTP_CLIENT_TOKEN instead of HttpClient
 * 3. Use the injected client normally - auth headers are added automatically
 *
 * To add additional interceptors:
 * ```typescript
 * @NgModule({
 *   providers: [
 *     {
 *       provide: ANGULAR_HTTP_CLIENT_INTERCEPTOR_TOKEN,
 *       useClass: MyCustomInterceptor,
 *       multi: true
 *     }
 *   ]
 * })
 * ```
 *
 * To use the Angular HttpClient in your services:
 * ```typescript
 * @Injectable({ providedIn: 'root' })
 * export class ApiService {
 *   constructor(
 *     @Inject(AUTHENTICATED_HTTP_CLIENT_TOKEN)
 *     private readonly httpClient: HttpClient
 *   ) {}
 *
 *   // All requests will automatically include Bearer token
 *   getUserProfile(): Observable<UserProfile> {
 *     return this.httpClient.get<UserProfile>('/api/user/profile');
 *   }
 *
 *   updateUserData(data: UserData): Observable<UserData> {
 *     return this.httpClient.put<UserData>('/api/user', data);
 *   }
 *
 *   // No need to manually add auth headers - done automatically!
 * }
 * ```
 */
@NgModule({
  providers: [
    {
      provide: ANGULAR_HTTP_CLIENT_INTERCEPTOR_TOKEN,
      useClass: AuthTokenInterceptor,
      multi: true,
    },
    {
      provide: ANGULAR_HTTP_CLIENT_TOKEN,
      useFactory: createHttpClient,
      deps: [HttpBackend, ANGULAR_HTTP_CLIENT_INTERCEPTOR_TOKEN],
    },
  ],
})
export class AngularHttpClientModule {}
